import { createContext, useContext, useEffect, useMemo, useState } from "react";
import { useContent } from "./content";

// Prices in the database are ALWAYS stored as EUR. This context turns EUR
// values into the customer-visible currency (EUR or HUF).
//
// - Admin sets the default currency + HUF exchange rate in the CMS.
//   (Admin → Tartalom → Pénznem, keys: `currency_default`, `currency_huf_rate`.)
// - The customer can override the choice in the navbar; the choice is
//   persisted in localStorage and survives reloads.
// - `useFormatMoney()` returns a function that reads the latest value from
//   the context (so components re-render on switch).
// - We also mirror the state onto `globalThis._b2bCurrency` so the legacy
//   bare `formatMoney(n)` helper in api.js stays working without touching
//   every call-site that already imports it.

const STORAGE_KEY = "b2b_currency";

const CurrencyContext = createContext({
  currency: "EUR",
  setCurrency: () => {},
  rateHuf: 400,
  format: (n) => `€${Number(n || 0).toFixed(2)}`,
});

export function CurrencyProvider({ children }) {
  const { t, loaded } = useContent();
  const cmsDefault = (t("currency_default", "EUR") || "EUR").toUpperCase() === "HUF" ? "HUF" : "EUR";
  const cmsRate = Number(t("currency_huf_rate", "400")) || 400;

  // Start with persisted user choice if any, otherwise the CMS default.
  const initial = (() => {
    if (typeof window === "undefined") return cmsDefault;
    const saved = window.localStorage.getItem(STORAGE_KEY);
    return saved === "HUF" || saved === "EUR" ? saved : cmsDefault;
  })();

  const [currency, setCurrencyState] = useState(initial);

  // Sync mirror IMMEDIATELY (not via useEffect) so bare formatMoney() calls
  // in child renders see the latest value. useEffect runs AFTER the children
  // render, which caused stale formatting on the first re-render after a switch.
  if (typeof globalThis !== "undefined") {
    globalThis._b2bCurrency = { currency, rateHuf: cmsRate };
  }

  // If the CMS default flips AND the user has no explicit override, follow it.
  useEffect(() => {
    if (!loaded) return;
    const saved = window.localStorage.getItem(STORAGE_KEY);
    if (!saved) setCurrencyState(cmsDefault);
  }, [cmsDefault, loaded]);

  const setCurrency = (next) => {
    setCurrencyState(next);
    try {
      window.localStorage.setItem(STORAGE_KEY, next);
    } catch { /* ignore quota issues */ }
  };

  const format = useMemo(() => {
    return (eur) => {
      if (eur == null || isNaN(eur)) return "—";
      const n = Number(eur);
      if (currency === "HUF") {
        // Round HUF to whole forint — no decimals in real-world Hungarian retail.
        return `${Math.round(n * cmsRate).toLocaleString("hu-HU")} Ft`;
      }
      return `€${n.toFixed(2)}`;
    };
  }, [currency, cmsRate]);

  const value = useMemo(
    () => ({ currency, setCurrency, rateHuf: cmsRate, format }),
    [currency, cmsRate, format],
  );

  return <CurrencyContext.Provider value={value}>{children}</CurrencyContext.Provider>;
}

export function useCurrency() {
  return useContext(CurrencyContext);
}

/**
 * Returns a function `(eurAmount) => formatted` bound to the current currency.
 * Use this in components that currently import `formatMoney` from `lib/api`.
 */
export function useFormatMoney() {
  return useCurrency().format;
}
